import React, { Fragment, useState, useEffect, useCallback } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import {
 Row, Col, Card, Table, Badge, Dropdown,
 Button, Form, Tab, Nav, Spinner,
} from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import swal from "sweetalert";
import { useSelector } from "react-redux";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";
import PermissionWrapper from "../PermissionWrapper";
import PortalMenu from "../Common/PortalMenu";
import api from "../../../api";
import {
 getSafetyIncidents, deleteSafetyIncident,
 resolveSafetyIncident, closeSafetyIncident, getSafetyStats,
} from "../../../services/safetyService";
import {
 getChecklists, deleteChecklist, approveChecklist,
 downloadChecklistExcel, getChecklistStats,
} from "../../../services/safetyChecklistService";
import SafetyViewModal from "./SafetyViewModal";

/* Constants */
const INC_STATUS_COLORS = {
 Draft: "secondary", Submitted: "warning",
 "Under Investigation": "info", Resolved: "success", Closed: "dark",
};
const SEVERITY_COLORS = {
 Low: "success", Medium: "warning", High: "danger", Critical: "dark",
};
const CL_STATUS_COLORS = {
 Draft: "secondary", Submitted: "warning",
 Reviewed: "info", Approved: "success", Closed: "dark",
};
const OVERALL_COLORS = {
 Safe: "success", "At Risk": "warning", Unsafe: "danger", Pending: "secondary",
};
const INCIDENT_TYPES = [
 "Near Miss", "First Aid", "Medical Treatment", "Lost Time Injury", "Fatality",
 "Property Damage", "Environmental", "Fire", "Electrical Hazard", "Chemical Spill",
 "Fall from Height", "Struck By Object", "Caught In/Between",
 "Safety Observation", "Unsafe Act", "Unsafe Condition",
 "Toolbox Talk", "Safety Audit", "Compliance Check", "Other",
];

const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24">
 <circle fill="#000000" cx="5" cy="12" r="2" />
 <circle fill="#000000" cx="12" cy="12" r="2" />
 <circle fill="#000000" cx="19" cy="12" r="2" />
 </svg>
);

/* 
 COMPONENT
 */
const SafetyList = () => {
 const navigate = useNavigate();
 const location = useLocation();
 const user = useSelector((s) => s.auth?.userDetails || {});

 // Default tab from navigation state (e.g. after returning from checklist form)
 const defaultTab = location.state?.tab || "incidents";

 /* Shared refs */
 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);

 useEffect(() => {
 (async () => {
 try {
 const [woRes, projRes] = await Promise.all([
 api.get("/work-orders?limit=500"),
 api.get("/projects?limit=500"),
 ]);
 setWorkOrders(woRes.data?.data || []);
 setProjects(projRes.data?.data || []);
 } catch (_) {}
 })();
 }, []);

 /* 
 TAB 1 — INCIDENTS
 */
 const [incidents, setIncidents] = useState([]);
 const [incLoading, setIncLoading] = useState(true);
 const [incStats, setIncStats] = useState(null);
 const [selectedRecord, setSelectedRecord] = useState(null);
 const [showViewModal, setShowViewModal] = useState(false);

 const [incPagination, setIncPagination] = useState({
 currentPage: 1, totalPages: 1, totalCount: 0, limit: 10,
 });
 const [incFilters, setIncFilters] = usePersistentFilters("safety_incidents_filters", {
 search: "", status: "", incidentType: "", severity: "", fromDate: "", toDate: "",
 });

 const fetchIncStats = useCallback(async () => {
 try {
 const res = await getSafetyStats();
 if (res.data.success) setIncStats(res.data.data);
 } catch (_) {}
 }, []);

 const fetchIncidents = useCallback(async () => {
 setIncLoading(true);
 try {
 const res = await getSafetyIncidents({
 page: incPagination.currentPage,
 limit: incPagination.limit,
 ...(incFilters.search && { search: incFilters.search }),
 ...(incFilters.status && { status: incFilters.status }),
 ...(incFilters.incidentType && { incidentType: incFilters.incidentType }),
 ...(incFilters.severity && { severity: incFilters.severity }),
 ...(incFilters.fromDate && { fromDate: incFilters.fromDate }),
 ...(incFilters.toDate && { toDate: incFilters.toDate }),
 });
 if (res.data.success) {
 setIncidents(res.data.data);
 setIncPagination((p) => ({
 ...p,
 totalPages: res.data.pagination.totalPages,
 totalCount: res.data.pagination.totalCount,
 currentPage: res.data.pagination.currentPage,
 }));
 }
 } catch (_) { toast.error("Failed to load incidents"); }
 finally { setIncLoading(false); }
 }, [incPagination.currentPage, incPagination.limit, incFilters]);

 useEffect(() => { fetchIncStats(); }, [fetchIncStats]);
 useEffect(() => { fetchIncidents(); }, [fetchIncidents]);

 const handleIncFilter = (name, value) => {
 setIncFilters((p) => ({ ...p, [name]: value }));
 setIncPagination((p) => ({ ...p, currentPage: 1 }));
 };

 const handleView = async (record) => {
 try {
 const { getSafetyIncident } = await import("../../../services/safetyService");
 const res = await getSafetyIncident(record._id);
 if (res.data.success) { setSelectedRecord(res.data.data); setShowViewModal(true); }
 } catch (_) { toast.error("Failed to load record"); }
 };

 const handleDeleteInc = async (id) => {
 if (!await swal({ title: "Delete Incident?", icon: "warning", buttons: true, dangerMode: true })) return;
 try { await deleteSafetyIncident(id); toast.success("Deleted"); fetchIncidents(); fetchIncStats(); }
 catch (_) { toast.error("Delete failed"); }
 };

 const handleResolve = async (id) => {
 const remarks = window.prompt("Resolution remarks (optional):");
 if (remarks === null) return;
 try { await resolveSafetyIncident(id, { approvalRemarks: remarks }); toast.success("Marked Resolved"); fetchIncidents(); fetchIncStats(); }
 catch (_) { toast.error("Failed"); }
 };

 const handleClose = async (id) => {
 const lessons = window.prompt("Lessons learned (optional):");
 if (lessons === null) return;
 try { await closeSafetyIncident(id, { lessonsLearned: lessons }); toast.success("Incident closed"); fetchIncidents(); fetchIncStats(); }
 catch (_) { toast.error("Failed"); }
 };

 /* 
 TAB 2 — COMPLIANCE CHECKLISTS
 */
 const [checklists, setChecklists] = useState([]);
 const [clLoading, setClLoading] = useState(true);
 const [clStats, setClStats] = useState(null);
 const [downloading, setDownloading] = useState(null);

 const [clPagination, setClPagination] = useState({
 currentPage: 1, totalPages: 1, totalCount: 0, limit: 10,
 });
 const [clFilters, setClFilters] = usePersistentFilters("safety_checklists_filters", {
 search: "", status: "", fromDate: "", toDate: "",
 });

 const fetchClStats = useCallback(async () => {
 try {
 const res = await getChecklistStats();
 if (res.data?.success) setClStats(res.data.data);
 } catch (_) {}
 }, []);

 const fetchChecklists = useCallback(async () => {
 setClLoading(true);
 try {
 const res = await getChecklists({
 page: clPagination.currentPage,
 limit: clPagination.limit,
 ...(clFilters.search && { search: clFilters.search }),
 ...(clFilters.status && { status: clFilters.status }),
 ...(clFilters.fromDate && { fromDate: clFilters.fromDate }),
 ...(clFilters.toDate && { toDate: clFilters.toDate }),
 });
 if (res.data.success) {
 setChecklists(res.data.data);
 setClPagination((p) => ({
 ...p,
 totalPages: res.data.pagination.totalPages,
 totalCount: res.data.pagination.totalCount,
 currentPage: res.data.pagination.currentPage,
 }));
 }
 } catch (_) { toast.error("Failed to load checklists"); }
 finally { setClLoading(false); }
 }, [clPagination.currentPage, clPagination.limit, clFilters]);

 useEffect(() => { fetchClStats(); }, [fetchClStats]);
 useEffect(() => { fetchChecklists(); }, [fetchChecklists]);

 const handleClFilter = (name, value) => {
 setClFilters((p) => ({ ...p, [name]: value }));
 setClPagination((p) => ({ ...p, currentPage: 1 }));
 };

 const handleDeleteCl = async (id) => {
 if (!await swal({ title: "Delete Checklist?", icon: "warning", buttons: true, dangerMode: true })) return;
 try { await deleteChecklist(id); toast.success("Deleted"); fetchChecklists(); fetchClStats(); }
 catch (_) { toast.error("Delete failed"); }
 };

 const handleApproveCl = async (id) => {
 const remarks = window.prompt("Approval remarks (optional):");
 if (remarks === null) return;
 try { await approveChecklist(id, { approvalRemarks: remarks }); toast.success("Approved"); fetchChecklists(); fetchClStats(); }
 catch (_) { toast.error("Approval failed"); }
 };

 const handleDownloadExcel = async (id, checklistNumber) => {
 setDownloading(id);
 try {
 const res = await downloadChecklistExcel(id);
 const url = URL.createObjectURL(new Blob([res.data]));
 const a = document.createElement("a");
 a.href = url;
 a.download = `Safety_Checklist_${checklistNumber || id}.xlsx`;
 a.click();
 URL.revokeObjectURL(url);
 } catch (_) { toast.error("Excel download failed"); }
 finally { setDownloading(null); }
 };

 /* 
 RENDER
 */
 return (
 <Fragment>
 <PageTitle activeMenu="Safety" motherMenu="Safety" />

 {/* Combined Stats Row */}
 <Row className="mb-3">
 {/* Incident stats */}
 {incStats && [
 { label: "Total Incidents", value: incStats.total, color: "primary", icon: "fa-shield-alt" },
 { label: "Open", value: incStats.open, color: "warning", icon: "fa-exclamation-circle" },
 { label: "Critical", value: incStats.critical, color: "danger", icon: "fa-skull-crossbones" },
 { label: "LTI", value: incStats.lostTimeInjuries, color: "dark", icon: "fa-user-injured" },
 { label: "Open CAs", value: incStats.openCorrectiveActions, color: "info", icon: "fa-tasks" },
 ].map((s) => (
 <Col key={s.label} xs={6} sm={4} md className="mb-3">
 <Card className={`bg-${s.color} text-white shadow-sm`}>
 <Card.Body className="p-3 d-flex justify-content-between align-items-center">
 <div>
 <p className="mb-0" style={{ fontSize: "0.73rem", opacity: 0.85 }}>{s.label}</p>
 <h4 className="mb-0 fw-bold">{s.value}</h4>
 </div>
 <i className={`fas ${s.icon} fa-2x opacity-50`} />
 </Card.Body>
 </Card>
 </Col>
 ))}
 {/* Checklist stats */}
 {clStats && [
 { label: "Checklists", value: clStats.total, color: "success", icon: "fa-clipboard-check" },
 { label: "Avg Score", value: `${clStats.avgComplianceScore}%`, color: "secondary", icon: "fa-percent" },
 ].map((s) => (
 <Col key={s.label} xs={6} sm={4} md className="mb-3">
 <Card className={`bg-${s.color} text-white shadow-sm`}>
 <Card.Body className="p-3 d-flex justify-content-between align-items-center">
 <div>
 <p className="mb-0" style={{ fontSize: "0.73rem", opacity: 0.85 }}>{s.label}</p>
 <h4 className="mb-0 fw-bold">{s.value}</h4>
 </div>
 <i className={`fas ${s.icon} fa-2x opacity-50`} />
 </Card.Body>
 </Card>
 </Col>
 ))}
 </Row>

 {/* Tabbed Card */}
 <Tab.Container defaultActiveKey={defaultTab}>
 <Card className="shadow-sm">
 <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2 pb-0">
 <Nav variant="tabs" className="border-bottom-0">
 <Nav.Item>
 <Nav.Link eventKey="incidents" className="fw-semibold">
 <i className="fas fa-hard-hat me-1 text-warning" /> Incidents
 {incStats?.open > 0 && (
 <Badge bg="warning" className="ms-1" style={{ fontSize: "0.65rem" }}>
 {incStats.open}
 </Badge>
 )}
 </Nav.Link>
 </Nav.Item>
 <Nav.Item>
 <Nav.Link eventKey="checklists" className="fw-semibold">
 <i className="fas fa-clipboard-check me-1 text-success" /> Compliance Checklists
 {clStats?.unsafe > 0 && (
 <Badge bg="danger" className="ms-1" style={{ fontSize: "0.65rem" }}>
 {clStats.unsafe}
 </Badge>
 )}
 </Nav.Link>
 </Nav.Item>
 </Nav>

 {/* Header action buttons — swap based on active tab via CSS trick; we use both and hide */}
 <div className="d-flex gap-2 flex-wrap align-items-center pb-2">
 <Tab.Content className="p-0 border-0 bg-transparent">
 {/* shown inside header using custom approach — use separate renders per tab instead */}
 </Tab.Content>
 </div>
 </Card.Header>

 <Tab.Content>

 {/* TAB 1 : INCIDENTS */}
 <Tab.Pane eventKey="incidents">
 {/* Sub-header */}
 <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-4 pt-3 pb-2">
 <span className="text-muted small">Daily incident reports, observations and corrective actions</span>
 <div className="d-flex gap-2">
 <Button variant="outline-primary" size="sm" onClick={() => navigate("/safety/report")}>
 <i className="fas fa-file-alt me-1" /> Report
 </Button>
 <PermissionWrapper user={user} required={["create:safety"]}>
 <Link className="btn btn-primary btn-sm" to="/safety/add">
 <i className="fas fa-plus me-1" /> Report Incident
 </Link>
 </PermissionWrapper>
 </div>
 </div>

 {/* Filters */}
 <div className="d-flex flex-wrap align-items-end gap-2 px-4 pb-3 border-bottom">
 <div className="d-flex flex-column" style={{ width: 170 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Search</label>
 <Form.Control size="sm" placeholder="Inc. No / location…"
 value={incFilters.search}
 onChange={(e) => handleIncFilter("search", e.target.value)} />
 </div>
 <div className="d-flex flex-column" style={{ width: 140 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Status</label>
 <Form.Select size="sm" value={incFilters.status}
 onChange={(e) => handleIncFilter("status", e.target.value)}>
 <option value="">All</option>
 {["Draft", "Submitted", "Under Investigation", "Resolved", "Closed"].map((s) => (
 <option key={s} value={s}>{s}</option>
 ))}
 </Form.Select>
 </div>
 <div className="d-flex flex-column" style={{ width: 170 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Type</label>
 <Form.Select size="sm" value={incFilters.incidentType}
 onChange={(e) => handleIncFilter("incidentType", e.target.value)}>
 <option value="">All Types</option>
 {INCIDENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
 </Form.Select>
 </div>
 <div className="d-flex flex-column" style={{ width: 110 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Severity</label>
 <Form.Select size="sm" value={incFilters.severity}
 onChange={(e) => handleIncFilter("severity", e.target.value)}>
 <option value="">All</option>
 {["Low", "Medium", "High", "Critical"].map((s) => <option key={s} value={s}>{s}</option>)}
 </Form.Select>
 </div>
 <div className="d-flex flex-column" style={{ width: 125 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">From</label>
 <Form.Control size="sm" type="date" value={incFilters.fromDate}
 onChange={(e) => handleIncFilter("fromDate", e.target.value)} />
 </div>
 <div className="d-flex flex-column" style={{ width: 125 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">To</label>
 <Form.Control size="sm" type="date" value={incFilters.toDate}
 onChange={(e) => handleIncFilter("toDate", e.target.value)} />
 </div>
 <div className="ms-auto d-flex flex-column" style={{ width: 65 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Show</label>
 <Form.Select size="sm" value={incPagination.limit}
 onChange={(e) => setIncPagination((p) => ({ ...p, currentPage: 1, limit: Number(e.target.value) }))}>
 {[10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
 </Form.Select>
 </div>
 </div>

 <Card.Body>
 {incLoading ? (
 <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
 ) : incidents.length === 0 ? (
 <div className="text-center py-5">
 <i className="fas fa-hard-hat fa-3x text-muted mb-3 d-block" />
 <h5 className="text-muted">No incidents found</h5>
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover className="align-middle mb-0">
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>Inc. No</th>
 <th>Date</th>
 <th>Type</th>
 <th>Location / Description</th>
 <th>Involved Person</th>
 <th>WO</th>
 <th>Severity</th>
 <th>Risk</th>
 <th>Status</th>
 <th>Action</th>
 </tr>
 </thead>
 <tbody>
 {incidents.map((rec, i) => (
 <tr key={rec._id}>
 <td>{(incPagination.currentPage - 1) * incPagination.limit + i + 1}</td>
 <td><strong className="text-primary">{rec.incidentNumber}</strong></td>
 <td>{new Date(rec.incidentDate).toLocaleDateString("en-IN")}</td>
 <td>
 <Badge bg="light" text="dark" className="border">{rec.incidentType}</Badge>
 </td>
 <td style={{ maxWidth: 200 }}>
 <div className="fw-semibold" style={{ fontSize: "0.85rem" }}>
 <i className="fas fa-map-marker-alt me-1 text-muted" />{rec.location}
 </div>
 {rec.description && (
 <small className="text-muted">
 {rec.description.slice(0, 50)}{rec.description.length > 50 ? "…" : ""}
 </small>
 )}
 </td>
 <td>
 {rec.involvedPerson
 ? <div><div className="fw-semibold">{rec.involvedPerson}</div>
 {rec.designation && <small className="text-muted">{rec.designation}</small>}
 </div>
 : <span className="text-muted">—</span>}
 </td>
 <td><small className="text-primary">{rec.workOrder?.woNo || "—"}</small></td>
 <td><Badge bg={SEVERITY_COLORS[rec.severity] || "secondary"}>{rec.severity}</Badge></td>
 <td>
 {rec.riskRating
 ? <Badge bg={SEVERITY_COLORS[rec.riskRating] || "secondary"}>{rec.riskRating}</Badge>
 : <span className="text-muted">—</span>}
 </td>
 <td><Badge bg={INC_STATUS_COLORS[rec.status] || "secondary"}>{rec.status}</Badge></td>
 <td>
 <Dropdown>
 <Dropdown.Toggle as="div" className="btn btn-link c-pointer i-false">{svgMenu}</Dropdown.Toggle>
 <Dropdown.Menu align="end" as={PortalMenu}>
 <PermissionWrapper user={user} required={["read:safety"]}>
 <Dropdown.Item onClick={() => handleView(rec)}>
 <i className="fas fa-eye me-2" /> View
 </Dropdown.Item>
 </PermissionWrapper>
 <PermissionWrapper user={user} required={["edit:safety"]}>
 <Dropdown.Item onClick={() => navigate(`/safety/edit/${rec._id}`)}>
 <i className="fas fa-edit me-2" /> Edit
 </Dropdown.Item>
 </PermissionWrapper>
 {["Draft", "Submitted", "Under Investigation"].includes(rec.status) && (
 <>
 <Dropdown.Divider />
 <PermissionWrapper user={user} required={["approve:safety"]}>
 <Dropdown.Item className="text-success" onClick={() => handleResolve(rec._id)}>
 <i className="fas fa-check me-2" /> Mark Resolved
 </Dropdown.Item>
 </PermissionWrapper>
 </>
 )}
 {rec.status === "Resolved" && (
 <PermissionWrapper user={user} required={["approve:safety"]}>
 <Dropdown.Item className="text-secondary" onClick={() => handleClose(rec._id)}>
 <i className="fas fa-lock me-2" /> Close Incident
 </Dropdown.Item>
 </PermissionWrapper>
 )}
 <PermissionWrapper user={user} required={["delete:safety"]}>
 <Dropdown.Divider />
 <Dropdown.Item className="text-danger" onClick={() => handleDeleteInc(rec._id)}>
 <i className="fas fa-trash me-2" /> Delete
 </Dropdown.Item>
 </PermissionWrapper>
 </Dropdown.Menu>
 </Dropdown>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 {!incLoading && incidents.length > 0 && (
 <AppPagination
 currentPage={incPagination.currentPage}
 totalPages={incPagination.totalPages}
 totalCount={incPagination.totalCount}
 itemsLength={incidents.length}
 limit={incPagination.limit}
 onPageChange={(page) => setIncPagination((p) => ({ ...p, currentPage: page }))}
 />
 )}
 </Tab.Pane>

 {/* TAB 2 : COMPLIANCE CHECKLISTS */}
 <Tab.Pane eventKey="checklists">
 {/* Sub-header */}
 <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-4 pt-3 pb-2">
 <span className="text-muted small">Daily / Weekly EHS compliance inspections — Sections A through H</span>
 <div className="d-flex gap-2">
 <PermissionWrapper user={user} required={["create:safety"]}>
 <Link className="btn btn-success btn-sm" to="/safety/checklists/add">
 <i className="fas fa-plus me-1" /> New Checklist
 </Link>
 </PermissionWrapper>
 </div>
 </div>

 {/* Filters */}
 <div className="d-flex flex-wrap align-items-end gap-2 px-4 pb-3 border-bottom">
 <div className="d-flex flex-column" style={{ width: 170 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Search</label>
 <Form.Control size="sm" placeholder="Checklist No. / Contract…"
 value={clFilters.search}
 onChange={(e) => handleClFilter("search", e.target.value)} />
 </div>
 <div className="d-flex flex-column" style={{ width: 140 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Status</label>
 <Form.Select size="sm" value={clFilters.status}
 onChange={(e) => handleClFilter("status", e.target.value)}>
 <option value="">All</option>
 {["Draft", "Submitted", "Reviewed", "Approved", "Closed"].map((s) => (
 <option key={s} value={s}>{s}</option>
 ))}
 </Form.Select>
 </div>
 <div className="d-flex flex-column" style={{ width: 125 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">From</label>
 <Form.Control size="sm" type="date" value={clFilters.fromDate}
 onChange={(e) => handleClFilter("fromDate", e.target.value)} />
 </div>
 <div className="d-flex flex-column" style={{ width: 125 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">To</label>
 <Form.Control size="sm" type="date" value={clFilters.toDate}
 onChange={(e) => handleClFilter("toDate", e.target.value)} />
 </div>
 <div className="ms-auto d-flex flex-column" style={{ width: 65 }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Show</label>
 <Form.Select size="sm" value={clPagination.limit}
 onChange={(e) => setClPagination((p) => ({ ...p, currentPage: 1, limit: Number(e.target.value) }))}>
 {[10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
 </Form.Select>
 </div>
 </div>

 <Card.Body>
 {clLoading ? (
 <div className="text-center py-5"><Spinner animation="border" variant="success" /></div>
 ) : checklists.length === 0 ? (
 <div className="text-center py-5">
 <i className="fas fa-clipboard-check fa-3x text-muted mb-3 d-block" />
 <h5 className="text-muted">No checklists found</h5>
 <p className="text-muted">Create the first EHS compliance checklist.</p>
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover className="align-middle mb-0">
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>Checklist No.</th>
 <th>Date</th>
 <th>Type</th>
 <th>Project</th>
 <th>Inspected By</th>
 <th className="text-center">Score</th>
 <th className="text-center">Overall</th>
 <th>Status</th>
 <th>Excel</th>
 <th>Action</th>
 </tr>
 </thead>
 <tbody>
 {checklists.map((rec, i) => (
 <tr key={rec._id}>
 <td>{(clPagination.currentPage - 1) * clPagination.limit + i + 1}</td>
 <td><strong className="text-success">{rec.checklistNumber}</strong></td>
 <td>{new Date(rec.inspectionDate).toLocaleDateString("en-IN")}</td>
 <td><Badge bg="light" text="dark" className="border">{rec.inspectionType}</Badge></td>
 <td>
 <div className="fw-semibold" style={{ fontSize: "0.85rem" }}>
 {rec.project?.projectName || rec.project?.name || "—"}
 </div>
 </td>
 <td>{rec.inspectedBy?.name || "—"}</td>
 <td className="text-center">
 <div className="fw-bold">{rec.complianceScore}%</div>
 <div style={{
 height: 5, borderRadius: 3, width: 60, margin: "2px auto",
 background: `linear-gradient(to right, ${
 rec.complianceScore >= 85 ? "#28a745"
 : rec.complianceScore >= 60 ? "#ffc107" : "#dc3545"
 } ${rec.complianceScore}%, #e9ecef ${rec.complianceScore}%)`,
 }} />
 <small className="text-muted" style={{ fontSize: "0.68rem" }}>
 {rec.compliantCount} {rec.nonCompliantCount} {rec.partialCount}
 </small>
 </td>
 <td className="text-center">
 <Badge bg={OVERALL_COLORS[rec.overallStatus] || "secondary"}>
 {rec.overallStatus}
 </Badge>
 </td>
 <td><Badge bg={CL_STATUS_COLORS[rec.status] || "secondary"}>{rec.status}</Badge></td>
 {/* Dedicated Excel download cell */}
 <td className="text-center">
 <Button
 variant="outline-success"
 size="sm"
 title="Download Excel"
 disabled={downloading === rec._id}
 onClick={() => handleDownloadExcel(rec._id, rec.checklistNumber)}
 style={{ padding: "2px 8px" }}
 >
 {downloading === rec._id
 ? <Spinner size="sm" />
 : <i className="fas fa-file-excel" />}
 </Button>
 </td>
 <td>
 <Dropdown>
 <Dropdown.Toggle as="div" className="btn btn-link c-pointer i-false">{svgMenu}</Dropdown.Toggle>
 <Dropdown.Menu align="end" as={PortalMenu}>
 <PermissionWrapper user={user} required={["edit:safety"]}>
 <Dropdown.Item onClick={() => navigate(`/safety/checklists/edit/${rec._id}`)}>
 <i className="fas fa-edit me-2" /> Edit / Fill
 </Dropdown.Item>
 </PermissionWrapper>
 {["Draft", "Submitted", "Reviewed"].includes(rec.status) && (
 <>
 <Dropdown.Divider />
 <PermissionWrapper user={user} required={["approve:safety"]}>
 <Dropdown.Item className="text-success" onClick={() => handleApproveCl(rec._id)}>
 <i className="fas fa-check me-2" /> Approve
 </Dropdown.Item>
 </PermissionWrapper>
 </>
 )}
 <PermissionWrapper user={user} required={["delete:safety"]}>
 <Dropdown.Divider />
 <Dropdown.Item className="text-danger" onClick={() => handleDeleteCl(rec._id)}>
 <i className="fas fa-trash me-2" /> Delete
 </Dropdown.Item>
 </PermissionWrapper>
 </Dropdown.Menu>
 </Dropdown>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 {!clLoading && checklists.length > 0 && (
 <AppPagination
 currentPage={clPagination.currentPage}
 totalPages={clPagination.totalPages}
 totalCount={clPagination.totalCount}
 itemsLength={checklists.length}
 limit={clPagination.limit}
 onPageChange={(page) => setClPagination((p) => ({ ...p, currentPage: page }))}
 />
 )}
 </Tab.Pane>

 </Tab.Content>
 </Card>
 </Tab.Container>

 {/* View Modal */}
 {selectedRecord && (
 <SafetyViewModal
 show={showViewModal}
 record={selectedRecord}
 onHide={() => { setShowViewModal(false); setSelectedRecord(null); }}
 onRefresh={() => { fetchIncidents(); fetchIncStats(); }}
 user={user}
 />
 )}
 </Fragment>
 );
};

export default SafetyList;
